import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators';
import { IInvestmentSuggestion } from '@/modules/wealth/services/investment';
import { GlobalSummarySuggestedPortfolioDetails } from '@/modules/common/services/graphql/server/server.sdk';
import { SuggestionType } from '@/modules/investments/types/suggestion';
import {
  getInvestmentSuggestionsList,
  getRedeemSuggestionDetail,
  getRedeemSuggestions,
  getWalletSuggestionDetail,
  getWalletSuggestionsList,
  handleSuggestionType
} from './helpers';
import { ISuggestionDetail, ISuggestions } from './types';

@Module({
  namespaced: true,
  name: 'SuggestionModule'
})
export class SuggestionStoreModule extends VuexModule {
  isFetching = false;
  error = false;
  validationError = {
    title: '',
    message: '',
    type: ''
  };

  suggestionType = SuggestionType.Wallet;
  suggestionDetails: ISuggestionDetail = {
    id: '',
    name: '',
    detail: undefined
  };

  suggestions: ISuggestions = {
    investmentList: { data: [], requestTerms: false, requestRisks: false },
    walletList: [],
    redeemList: [],
    status: ''
  };

  @Mutation
  public setValidationErrorMessage(message: string) {
    this.validationError.message = message;
  }

  @Mutation
  public setValidationErrorTitle(title: string) {
    this.validationError.title = title;
  }

  @Mutation
  public setValidationErrorType(type: string) {
    this.validationError.type = type;
  }

  @Mutation
  private setSuggestions(suggestions: ISuggestions) {
    this.suggestions = suggestions;
  }

  @Mutation
  private setFetching(isFetching: boolean) {
    this.isFetching = isFetching;
  }

  @Mutation
  private setError(error: boolean) {
    this.error = error;
  }

  @Mutation
  public setSuggestionType(suggestionType: SuggestionType) {
    this.suggestionType = suggestionType;
  }

  @Mutation
  public setDetails(
    details: IInvestmentSuggestion | GlobalSummarySuggestedPortfolioDetails
  ) {
    this.suggestionDetails = { ...this.suggestionDetails, detail: details };
  }

  @Mutation
  public resetData() {
    this.error = false;
    this.validationError = {
      title: '',
      message: '',
      type: ''
    };
    this.suggestionDetails = {
      id: '',
      name: '',
      detail: undefined
    };
  }

  @Mutation
  public async setSuggetionSimpleInfo(id: string) {
    const name = (await handleSuggestionType(this.suggestionType, {
      onInvestment: () =>
        this.suggestions.investmentList?.data.find(
          item => item.transactionId === id
        )?.portfolioName || '',
      onDefault: () =>
        this.suggestions.walletList?.find(item => item.id === id)?.name || ''
    })) as string;
    this.suggestionDetails = { ...this.suggestionDetails, id, name };
  }

  @Mutation
  removeSuggestion(suggestionId: string) {
    handleSuggestionType(this.suggestionType, {
      onInvestment: () => {
        this.suggestions.investmentList!.data =
          this.suggestions.investmentList?.data.filter(
            item => item.transactionId !== suggestionId
          )!;
      },
      onDefault: () =>
        (this.suggestions.walletList = this.suggestions.walletList?.filter(
          item => item.id !== suggestionId
        )),
      onRedeem: () => {
        this.suggestions.redeemList = this.suggestions.redeemList?.filter(
          item => item.transactionId !== suggestionId
        );
      }
    });
  }

  @Action
  async getSuggestionDetails({
    suggestionId,
    customerId
  }: {
    suggestionId: string;
    customerId: string;
  }) {
    this.context.commit('setFetching', true);
    this.context.commit('setError', false);
    this.context.commit('setSuggetionSimpleInfo', suggestionId);
    try {
      const data = (await handleSuggestionType(this.suggestionType, {
        onInvestment: () =>
          this.suggestions.investmentList?.data.find(
            (item: IInvestmentSuggestion) => item.transactionId === suggestionId
          ),
        onDefault: () => getWalletSuggestionDetail(suggestionId),
        onRedeem: () =>
          getRedeemSuggestionDetail(
            customerId,
            this.suggestions.redeemList?.find(
              redeemSuggestion => redeemSuggestion.portfolio.id === suggestionId
            )!
          )
      })) as IInvestmentSuggestion | GlobalSummarySuggestedPortfolioDetails;

      this.context.commit('setDetails', data);
    } catch (err) {
      this.context.commit('setError', true);

      if (this.suggestionType === SuggestionType.Redeem) {
        this.context.commit(
          'setValidationErrorMessage',
          'Um ou mais produtos desta carteira não estão mais disponíveis para resgate. Não se preocupe, a solicitação será recusada automaticamente.'
        );
        this.context.commit('setValidationErrorTitle', 'Sugestão inválida');
      }
    } finally {
      this.context.commit('setFetching', false);
    }
  }

  @Action
  async fetchSuggestions(customerId: string) {
    this.context.commit('setFetching', true);
    this.context.commit('setError', false);
    try {
      let data: ISuggestions = {};
      data = (await handleSuggestionType(this.suggestionType, {
        onInvestment: () => getInvestmentSuggestionsList(customerId),
        onDefault: () => getWalletSuggestionsList(),
        onRedeem: () => getRedeemSuggestions()
      })) as ISuggestions;
      this.context.commit('setSuggestions', data);
    } catch (err) {
      this.context.commit('setError', true);
    } finally {
      this.context.commit('setFetching', false);
    }
  }
}
